/**
 * SKOP AI Alerts Panel
 *
 * Display and manage AI-generated alerts.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAIAlerts,
  markAlertRead,
  markAllAlertsRead,
  dismissAlert,
  getUnreadAlertCount,
  generateMockAIAlerts,
  type AIAlert,
  type AIAlertCategory,
  getCategoryLabel,
  getCategoryIcon,
  getSeverityColor,
  formatAlertTime,
} from '../lib/ai-alerts';

interface AIAlertsPanelProps {
  walletAddress: string;
  tokenAddress?: string;
  tokenSymbol?: string;
  tokenName?: string;
  compact?: boolean;
}

const CATEGORY_FILTERS: Array<{ id: AIAlertCategory | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'whale_movement', label: 'Whales' },
  { id: 'unusual_volume', label: 'Volume' },
  { id: 'risk_change', label: 'Risk' },
  { id: 'social_buzz', label: 'Social' },
  { id: 'holder_change', label: 'Holders' },
  { id: 'creator_activity', label: 'Creator' },
];

export function AIAlertsPanel({
  walletAddress,
  tokenAddress,
  tokenSymbol,
  tokenName,
  compact = false,
}: AIAlertsPanelProps) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AIAlert[]>([]);
  const [filter, setFilter] = useState<AIAlertCategory | 'all'>('all');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Generate mock alerts if token provided
    if (tokenAddress && tokenSymbol && tokenName) {
      generateMockAIAlerts(walletAddress, tokenAddress, tokenSymbol, tokenName);
    }

    // Load alerts
    loadAlerts();
  }, [walletAddress, tokenAddress, tokenSymbol, tokenName]);

  const loadAlerts = () => {
    let allAlerts = getAIAlerts(walletAddress);

    // Filter by token if provided
    if (tokenAddress) {
      allAlerts = allAlerts.filter(a => a.tokenAddress === tokenAddress);
    }

    // Filter out dismissed
    allAlerts = allAlerts.filter(a => !a.dismissed);

    setAlerts(allAlerts);
    setUnreadCount(getUnreadAlertCount(walletAddress));
  };

  const handleMarkRead = (alertId: string) => {
    markAlertRead(walletAddress, alertId);
    loadAlerts();
  };

  const handleDismiss = (alertId: string) => {
    dismissAlert(walletAddress, alertId);
    loadAlerts();
  };

  const handleMarkAllRead = () => {
    markAllAlertsRead(walletAddress);
    loadAlerts();
  };

  const handleViewToken = (address: string) => {
    navigate(`/${address}`);
  };

  const filteredAlerts = filter === 'all'
    ? alerts
    : alerts.filter(a => a.category === filter);

  if (compact && alerts.length === 0) {
    return null;
  }

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '14px', height: '14px', display: 'inline-flex', color: 'var(--accent)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
              <circle cx="8" cy="14" r="2"/>
              <circle cx="16" cy="14" r="2"/>
            </svg>
          </span>
          <span style={{ fontSize: '13px', fontWeight: 600 }}>AI Alerts</span>
          {unreadCount > 0 && (
            <span style={{
              padding: '2px 8px',
              fontSize: '11px',
              fontWeight: 600,
              background: 'var(--accent)',
              color: '#000',
              borderRadius: '10px',
            }}>
              {unreadCount}
            </span>
          )}
          <span style={{
            padding: '2px 6px',
            fontSize: '10px',
            fontWeight: 600,
            background: 'rgba(0, 255, 136, 0.1)',
            color: '#00ff88',
            borderRadius: 'var(--radius-sm)',
          }}>
            PRO
          </span>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      {!compact && (
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '12px',
          overflowX: 'auto',
          paddingBottom: '4px',
        }}>
          {CATEGORY_FILTERS.map(({ id, label }) => {
            const count = id === 'all'
              ? alerts.length
              : alerts.filter(a => a.category === id).length;

            return (
              <button
                key={id}
                onClick={() => setFilter(id)}
                style={{
                  padding: '6px 10px',
                  fontSize: '11px',
                  fontWeight: 500,
                  background: filter === id ? 'var(--accent-muted)' : 'var(--bg-tertiary)',
                  color: filter === id ? 'var(--accent)' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Alerts List */}
      {filteredAlerts.length === 0 ? (
        <div style={{
          padding: '24px',
          textAlign: 'center',
          color: 'var(--text-tertiary)',
          fontSize: '13px',
        }}>
          No AI alerts{filter !== 'all' ? ` for ${getCategoryLabel(filter)}` : ''}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          {filteredAlerts.slice(0, compact ? 3 : 10).map((alert) => (
            <AlertItem
              key={alert.id}
              alert={alert}
              showToken={!tokenAddress}
              onRead={() => handleMarkRead(alert.id)}
              onDismiss={() => handleDismiss(alert.id)}
              onViewToken={() => handleViewToken(alert.tokenAddress)}
            />
          ))}
        </div>
      )}

      {/* View More */}
      {compact && alerts.length > 3 && (
        <button
          onClick={() => navigate('/alerts')}
          style={{
            width: '100%',
            marginTop: '12px',
            padding: '10px',
            fontSize: '12px',
            fontWeight: 600,
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          View All Alerts ({alerts.length})
        </button>
      )}
    </div>
  );
}

function CategoryIcon({ category }: { category: AIAlert['category'] }) {
  const iconProps = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

  const icons: Record<string, React.ReactNode> = {
    'chart-bar': (
      <svg {...iconProps}>
        <path d="M3 3v18h18"/>
        <path d="M18 17V9"/>
        <path d="M13 17V5"/>
        <path d="M8 17v-3"/>
      </svg>
    ),
    'trending-up': (
      <svg {...iconProps}>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    'users': (
      <svg {...iconProps}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    'message-circle': (
      <svg {...iconProps}>
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
      </svg>
    ),
    'alert-triangle': (
      <svg {...iconProps}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    'user': (
      <svg {...iconProps}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  };

  const iconKey = getCategoryIcon(category);
  return (
    <span style={{ width: '16px', height: '16px', display: 'inline-flex', color: getSeverityColor('info') }}>
      {icons[iconKey] || icons['alert-triangle']}
    </span>
  );
}

function AlertItem({
  alert,
  showToken,
  onRead,
  onDismiss,
  onViewToken,
}: {
  alert: AIAlert;
  showToken: boolean;
  onRead: () => void;
  onDismiss: () => void;
  onViewToken: () => void;
}) {
  return (
    <div
      style={{
        padding: '12px',
        background: alert.read ? 'var(--bg-tertiary)' : 'rgba(0, 240, 255, 0.05)',
        borderRadius: 'var(--radius-md)',
        borderLeft: `3px solid ${getSeverityColor(alert.severity)}`,
      }}
      onClick={!alert.read ? onRead : undefined}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '8px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CategoryIcon category={alert.category} />
          <div>
            <div style={{
              fontWeight: 600,
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              {alert.title}
              {!alert.read && (
                <span style={{
                  width: '6px',
                  height: '6px',
                  background: 'var(--accent)',
                  borderRadius: '50%',
                }} />
              )}
            </div>
            {showToken && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewToken();
                }}
                style={{
                  padding: 0,
                  fontSize: '11px',
                  color: 'var(--accent)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {alert.tokenSymbol} →
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
          }}>
            {formatAlertTime(alert.createdAt)}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            style={{
              padding: '2px 6px',
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      </div>

      <div style={{
        fontSize: '12px',
        color: 'var(--text-secondary)',
        marginBottom: '8px',
      }}>
        {alert.description}
      </div>

      <div style={{
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        padding: '8px',
        background: 'var(--surface-secondary)',
        borderRadius: 'var(--radius-sm)',
        fontStyle: 'italic',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffb800" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18h6"/>
            <path d="M10 22h4"/>
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
          </svg>
          {alert.aiInsight}
        </span>
      </div>
    </div>
  );
}
